import React from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { setSettings } from '../../../../support/slices/tileBuilderSlice';
import { ChartControlProps } from './ChartControlProps';

const LABEL_MODES = [
  { value: 'none', label: 'None' },
  { value: 'values', label: 'Per-segment values' },
  { value: 'totals', label: 'Totals only' },
  { value: 'both', label: 'Both values + totals' },
] as const;

const VALUE_FORMATS = [
  { value: '', label: 'Default' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'percent', label: 'Percent' },
] as const;

const TOTALS_MODE = [
  { value: 'auto', label: 'Auto (top for vertical, right for horizontal)' },
  { value: 'top', label: 'Top label (vertical)' },
  { value: 'right', label: 'Right label (horizontal)' },
] as const;

const NOTATION_MODES = [
  { value: 'standard', label: 'Standard' },
  { value: 'compact', label: 'Compact (K / M / B)' },
] as const;

const BarLabelsTotalsControls: React.FC<ChartControlProps> = ({ chartId, settings, dispatch }) => {
  const s: any = settings as any;
  const labelMode = (s.barLabelMode ?? 'none') as (typeof LABEL_MODES)[number]['value'];

  const showValues = labelMode === 'values' || labelMode === 'both';
  const showTotals = labelMode === 'totals' || labelMode === 'both';

  const notation = (s.numberNotation ?? 'standard') as 'standard' | 'compact';

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
      <Typography variant="subtitle2" sx={{ width: '100%' }}>
        Bar Labels
      </Typography>

      <FormControl sx={{ minWidth: 260 }} size="small">
        <InputLabel>Label Mode</InputLabel>
        <Select
          label="Label Mode"
          value={labelMode}
          onChange={(e) =>
            dispatch(setSettings({ chartId, settings: { barLabelMode: e.target.value as any } as any }))
          }
        >
          {LABEL_MODES.map((m) => (
            <MenuItem key={m.value} value={m.value}>
              {m.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ minWidth: 220 }} size="small">
        <InputLabel>Value Format</InputLabel>
        <Select
          label="Value Format"
          value={s.valueFormat ?? ''}
          onChange={(e) =>
            dispatch(setSettings({ chartId, settings: { valueFormat: e.target.value as any } as any }))
          }
        >
          {VALUE_FORMATS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {s.valueFormat === 'currency' && (
        <TextField
          label="Currency"
          size="small"
          value={s.currency ?? 'USD'}
          onChange={(e) => dispatch(setSettings({ chartId, settings: { currency: e.target.value } as any }))}
          sx={{ width: 140 }}
        />
      )}

      <FormControl sx={{ minWidth: 240 }} size="small">
        <InputLabel>Number Notation</InputLabel>
        <Select
          label="Number Notation"
          value={notation}
          onChange={(e) =>
            dispatch(
              setSettings({
                chartId,
                settings: { numberNotation: e.target.value as 'standard' | 'compact' } as any,
              })
            )
          }
        >
          {NOTATION_MODES.map((m) => (
            <MenuItem key={m.value} value={m.value}>
              {m.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {notation === 'compact' && (
        <>
          <TextField
            label="Compact Threshold"
            size="small"
            type="number"
            value={s.compactThreshold ?? 1000}
            onChange={(e) =>
              dispatch(
                setSettings({
                  chartId,
                  settings: { compactThreshold: Number(e.target.value) } as any,
                })
              )
            }
            sx={{ width: 180 }}
            helperText="Min abs value for K/M/B"
          />
          <TextField
            label="Compact Decimals"
            size="small"
            type="number"
            value={s.compactDecimals ?? 1}
            onChange={(e) =>
              dispatch(
                setSettings({
                  chartId,
                  settings: { compactDecimals: Number(e.target.value) } as any,
                })
              )
            }
            sx={{ width: 170 }}
            inputProps={{ min: 0, max: 6 }}
          />

          {s.valueFormat === 'currency' && (
            <FormControlLabel
              control={
                <Switch
                  checked={s.prefixCurrencyOnCompact !== false}
                  onChange={(e) =>
                    dispatch(
                      setSettings({
                        chartId,
                        settings: { prefixCurrencyOnCompact: e.target.checked } as any,
                      })
                    )
                  }
                />
              }
              label="Prefix currency in compact"
            />
          )}
        </>
      )}

      {showValues && (
        <>
          <TextField
            label="Value Label Skip Height"
            size="small"
            type="number"
            value={s.labelSkipHeight ?? 30}
            onChange={(e) =>
              dispatch(setSettings({ chartId, settings: { labelSkipHeight: Number(e.target.value) } as any }))
            }
            sx={{ width: 200 }}
            helperText="0 = always show"
          />
          <TextField
            label="Value Label Skip Width"
            size="small"
            type="number"
            value={s.labelSkipWidth ?? 0}
            onChange={(e) =>
              dispatch(setSettings({ chartId, settings: { labelSkipWidth: Number(e.target.value) } as any }))
            }
            sx={{ width: 200 }}
            helperText="0 = always show"
          />
        </>
      )}

      {showTotals && (
        <>
          <FormControl sx={{ minWidth: 260 }} size="small">
            <InputLabel>Totals Placement</InputLabel>
            <Select
              label="Totals Placement"
              value={s.totalsMode ?? 'auto'}
              onChange={(e) =>
                dispatch(setSettings({ chartId, settings: { totalsMode: e.target.value as any } as any }))
              }
            >
              {TOTALS_MODE.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Totals Offset (px)"
            size="small"
            type="number"
            value={s.totalsOffset ?? 10}
            onChange={(e) =>
              dispatch(setSettings({ chartId, settings: { totalsOffset: Number(e.target.value) } as any }))
            }
            sx={{ width: 180 }}
          />
        </>
      )}
    </Box>
  );
};

export default BarLabelsTotalsControls;